
import Alert from "@/components/Alert";
import AreaChart from "@/components/chart/AreaChart";
import AddPortfolio from "@/components/portfolio/AddPortfolio.vue";
import AddPositionToPortfolio from '@/components/portfolio/AddPositionToPortfolio';
import Quote from "@/components/portfolio/Quote";
import IntroductionCreatePortfolioModal from "@/components/portfolio/create/IntroductionCreatePortfolioModal";
import IntroductionPortfolioTourModal from "@/components/portfolio/introduction/IntroductionTourModal";
import GlobalPosition from "@/components/portfolio/position/GlobalPosition";
import ESGRiskRating from "@/components/share/ESGRiskRating";
import InvestedFunds from "@/components/share/InvestedFunds";
import PriceTargetConsensusChart from "@/components/share/PriceTargetConsensusChart";
import ShareDetails from "@/components/share/ShareDetails";
import ShareDividends from "@/components/share/ShareDividends";
import ShareFloatAndLiquidity from "@/components/share/ShareFloatAndLiquidity";
import ShareFundamentals from "@/components/share/ShareFundamentals";
import ShareGradeSummary from "@/components/share/ShareGradeSummary";
import ShareIdChanges from "@/components/share/ShareIdChanges";
import ShareIntervalSelector from "@/components/share/ShareIntervalSelector";
import ShareNews from "@/components/share/ShareNews";
import ShareQuotes from "@/components/share/ShareQuotes";
import ShareSimilarShares from "@/components/share/ShareSimilarShares";
import ShareSplits from "@/components/share/ShareSplits";
import ShareTickers from "@/components/share/ShareTickers";
import FundData from "@/components/share/fund/FundData";
import FundTables from "@/components/share/fund/FundTables";
import FinancialIndicatorIcon from "@/components/share/icons/FinancialIndicatorIcon.vue";
import NewsIcon from "@/components/share/icons/NewsIcon.vue";
import OverviewIcon from "@/components/share/icons/OverviewIcon.vue";
import { backendExceptionHandler, companyLogoHandler } from "@/mixins";
import CdnService from "@/service//CdnService";
import DividendService from "@/service/DividendService";
import ShareService from '@/service/ShareService';
import Vue from "vue";

const shareTabConstant = {
    overview: '0',
    financialFigures: '1',
    news: '2'
};

const fundamentalUrlToTabNameMapping = {
    kennzahlen: 'Übersicht',
    'gewinn-und-verlust': 'Gewinn und Velust',
    bilanz: 'Bilanz',
    cashflow: 'Cashflow',
    ergebnis: 'Ergebnis',
};

export default {
    name: "SharePage",

    components: {
        Alert,
        FundTables,
        GlobalPosition,
        Quote,
        AreaChart,
        InvestedFunds,
        ShareDetails,
        ShareNews,
        ShareFundamentals,
        ShareQuotes,
        ShareDividends,
        ShareSplits,
        ShareTickers,
        ShareIdChanges,
        ShareSimilarShares,
        FundData,
        AddPositionToPortfolio,
        AddPortfolio,
        IntroductionCreatePortfolioModal,
        IntroductionPortfolioTourModal,
        ShareIntervalSelector,
        ShareGradeSummary,
        PriceTargetConsensusChart,
        ESGRiskRating,
        ShareFloatAndLiquidity,
        OverviewIcon,
        FinancialIndicatorIcon,
        NewsIcon,
    },

    mixins: [backendExceptionHandler, companyLogoHandler],

    async fetch() {
        const routeLang = this.$route.path.split('/')?.[1];
        if (this.$getAvailableLocaleCodes().includes(routeLang)) {
            this.$setLocale(routeLang)
        }
        const url = '/api/shares/details'
        const params = {
            slug: this.slug,
            context: this.$store.state.preferences.isSandboxMode ? 'virtual' : 'default'
        }
        this.loading = true

        try {
            const response = await this.$axios.get(url, { params })

            this.share = response.data
            this.isDelisted = !this.share.active;

            await Promise.all([
                this.fetchGradeSummary(),
                this.fetchESGRiskRating(),
                this.fetchShareFloatData(),
                this.fetchDividends(),
                this.prefetchFundamentals(),
                this.queryQuotesMultimic(),
            ]);            
        } catch (error) {
            this.handleBackendException(error, error.message)
        } finally {
            this.initialized = true
            this.loading = false
            this.$gtag?.event('select_content', { content_type: 'share', content_id: this.share.isin })
        }
    },

    head() {
        if (!this.share) {
            return
        }
        const prefixes = {
            '1': this.$t('component.share.sharePage.financialFigures'),
            '2': this.$t('component.share.sharePage.news'),
        }
        const titlePrefix = this.activeTab > 0
            ? `${prefixes[this.activeTab]} - `
            : '';

        let title = this.share.pageTitle
            ? this.share.pageTitle
            : `${titlePrefix}${this.share.name} ${this.share.type} - ${this.$t('component.share.sharePage.companyDataAndPrices')} - ${this.share.isin} | MoneyPeak`;

        if (this.fundamentalTabId) {
            const tabName = fundamentalUrlToTabNameMapping[this.fundamentalTabId];
            title = `${this.share.name} ${this.share.type} - ${this.$t('component.share.sharePage.companyDataAndPrices')} - ${this.share.isin} - ${this.$t('component.share.sharePage.fundamentalData')} - ${tabName} | MoneyPeak`;
        }

        const description = `${this.share.name} ${this.share.type} ` +
            ` - ${this.$t('component.share.sharePage.companyDataAndPrices')}. ` +
            `${this.$t('component.share.sharePage.financialPlatformDescription')} | ` +
            `${this.$t('component.share.sharePage.fromSecuritiesToCrypto')}. ` +
            this.share.isin;

        return {
            titleTemplate: '',
            title,
            meta: [{
                hid: 'description',  // hid is a unique identifier
                name: 'description',
                content: description,
            }, {
                hid: 'og:title',
                name: 'og:title',
                content: title,
            }, {
                hid: 'og:description',
                name: 'og:description',
                content: description,
            }, {
                hid: 'twitter:title',
                name: 'twitter:title',
                content: title,
            }, {
                hid: 'twitter:description',
                name: 'twitter:description',
                content: description,
            }],
        }
    },

    props: {
        fundamentalTabId: {
            type: String,
            required: false,
        },
    },

    data() {
        return {
            share: null,
            refreshTimerId: null,
            activeRequest: null,
            initialized: false,
            activeTab: this.fundamentalTabId ? shareTabConstant.financialFigures : shareTabConstant.overview,
            loading: true,
            errorMessage: null,
            upsertedPortfolioId: null,
            quotes: [],
            interval: this.$store.state.preferences.activeChartInterval,
            chartType: this.$store.state.preferences.chartTypeShareDetailsSelected,
            isVisibleUserTour: false,
            shareTabConstant: shareTabConstant,
            hasFundamentalData: false,
            isPrefetchedFundamentals: false,
            isDelisted: false,
            gradeSummaryData: null,
            esgData: null,
            shareFloatData: null,
            dividends: [],
        }
    },

    computed: {
        isLoggedIn() {
            return this.$store.state.account.user
        },
        slug() {
            return this.$route.params.share
        },
        isShowNewUserTour() {
            return this.$store.state.account.showNewUsersTour;
        },
        intervals() {
            return [
                {
                    interval: 'day',
                    text: this.$t('component.shareIntervalSelector.intervals.day'),
                    valueFieldName: 'day',
                },
                {
                    interval: 'week',
                    text: this.$t('component.shareIntervalSelector.intervals.week'),
                    valueFieldName: 'week',
                },
                {
                    interval: 'month',
                    text: this.$t('component.shareIntervalSelector.intervals.month'),
                    valueFieldName: 'month',
                },
                {
                    interval: '3m',
                    text: this.$t('component.shareIntervalSelector.intervals.3months'),
                    valueFieldName: 'threeMonths',
                },
                {
                    interval: '6m',
                    text: this.$t('component.shareIntervalSelector.intervals.6months'),
                    valueFieldName: 'sixMonths',
                },
                {
                    interval: 'ytd',
                    text: this.$t('component.shareIntervalSelector.intervals.yearToDate'),
                    valueFieldName: 'ytd',
                },
                {
                    interval: 'year',
                    text: this.$t('component.shareIntervalSelector.intervals.year'),
                    valueFieldName: 'year',
                },
                {
                    interval: '2y',
                    text: this.$t('component.shareIntervalSelector.intervals.2years'),
                    valueFieldName: '2year',
                },
                {
                    interval: '3y',
                    text: this.$t('component.shareIntervalSelector.intervals.3years'),
                    valueFieldName: '3year',
                },
                {
                    interval: '4y',
                    text: this.$t('component.shareIntervalSelector.intervals.4years'),
                    valueFieldName: '4year',
                },
                {
                    interval: 'fiveyears',
                    text: this.$t('component.shareIntervalSelector.intervals.5years'),
                    valueFieldName: 'fiveYears',
                },
                {
                    interval: '10y',
                    text: this.$t('component.shareIntervalSelector.intervals.10years'),
                    valueFieldName: '10year',
                },
                {
                    interval: '20y',
                    text: this.$t('component.shareIntervalSelector.intervals.20years'),
                    valueFieldName: '20year',
                },
                {
                    interval: 'max',
                    text: this.$t('component.shareIntervalSelector.intervals.max'),
                    valueFieldName: 'max',
                }
            ]
        }
    },

    methods: {
        async queryQuotesMultimic() {
            return this
                .$axios
                .get('/api/quotes/current/multimic?isin=' + this.share.isin)
                .then(response => {
                    this.quotes = response.data
                })
                .catch(error => {
                    this.quotes = []
                    this.handleBackendException(error, this.$t('component.share.sharePage.errors.exchangeRatesLoadFailed'))
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        positionUpserted(portfolioId) {
            this.upsertedPortfolioId = portfolioId
            setTimeout(() => {
                this.upsertedPortfolioId = null
            }, 10000);
        },

        toPortfolio(portfolioId) {
            return {
                name: this.$getTranslatedRouteName('finance-portfolio-portfolio_id-tab_id'),
                params: { portfolio_id: `${portfolioId}` },
            }
        },

        onInfoInput(visible) {
            if (!visible) {
                this.upsertedPortfolioId = null
            }
        },

        copyToClipboard(copyText) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(copyText)
            }
        },

        openIntroPortfolio(index) {
            this.$root.$emit('open-intro-create-portfolio-dialog', index);
        },


        reopenCreatePortfolio() {
            this.$root.$emit('request-add-portfolio-dialog');
        },
        updateErrorMessage() {
            this.errorMessage = ""
        },
        handleRefreshShareData() {
            this.$fetch()
        },

        async prefetchFundamentals() {
            const shouldPrefetchFundamentals =
                this.share
                && this.share.type === 'Aktie'
                && !this.fundamentalTabId
                && !this.isPrefetchedFundamentals;

            if (!shouldPrefetchFundamentals) {
                return;
            }
            this.isPrefetchedFundamentals = true;
            const url = `/api/shares/fundamentals/keyMetrics/${this.share.isin}?period=annual`;

            try {
                const response = await this.$axios.get(url);
                this.hasFundamentalData = !!response.data;
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    this.hasFundamentalData = false;
                }
            }
        },

        async fetchGradeSummary() {
            try {
                this.gradeSummaryData = null;
                const data = await this.shareService.getShareGradeConsensus(this.share.isin);
                this.gradeSummaryData = data;
            } catch (error) {
                this.gradeSummaryData = null;
            }
        },

        async fetchESGRiskRating() {
            try {
                this.esgData = null;
                const data = await this.shareService.getShareESGRiskRating(this.share.isin);
                this.esgData = data;
            } catch (error) {
                this.esgData = null;
            }
        },

        async fetchShareFloatData() {
            try {
                this.shareFloatData = null;
                const data = await this.shareService.getShareFloat(this.share.isin);
                this.shareFloatData = data;
            } catch (error) {
                this.shareFloatData = null;
            }
        },

        async fetchDividends() {
            try {
                this.dividends = [];
                const data = await this.dividendService.getShareDividends(this.share.isin);
                this.dividends = data;
            } catch (error) {
                this.dividends = [];
            }
        }
    },

    created() {
        this.portfolioBus = new Vue()
        this.cdnService = new CdnService();
        this.shareService = new ShareService(this.$axios);
        this.dividendService = new DividendService(this.$axios, this.$t)
        this.$root.$on('transactions-load', this.$fetch)
    },

    async beforeMount() {
        this.refreshTimerId = setInterval(this.refresh, 30000)
        this.$root.$emit('portfolio-selected', null)
        this.$root.$emit('request-rewind-slider')
        this.$root.$on('portfolio-intro-open', this.openIntroPortfolio)
    },

    mounted() {
        this.interval = this.$store.getters["preferences/getChartActiveInterval"];
    },

    watch: {
        isShowNewUserTour(value) {
            this.isVisibleUserTour = value;
        },
        async slug() {
            await this.$fetch();
            this.activeTab = shareTabConstant.overview;
            if (this.fundamentalTabId && this.initialized) {
                this.activeTab = shareTabConstant.financialFigures;
            }
        },
        share() {
            this.$root.$emit('selected-share', this.share)
        },
        chartType(newValue) {
            this.$store.commit('preferences/setChartTypeShareDetailsSelected', newValue)
        },
        fundamentalTabId(tabId) {
            if (tabId && this.initialized) {
                this.activeTab = shareTabConstant.financialFigures;
            }
        },
        initialized(state) {
            if (state && this.fundamentalTabId) {
                this.activeTab = shareTabConstant.financialFigures;
            }
        },
        isDelisted(newValue) {
            if (newValue) {
                this.$store.commit('preferences/activeChartInterval', 'max')
            }
        },
        '$store.state.preferences.activeChartInterval'(newValue) {
            this.interval = newValue;
        },
    },

    beforeDestroy() {
        clearInterval(this.refreshTimerId)
        this.$root.$off('transactions-load')
    }
}
